import { useEffect, useRef } from 'react';

interface UseBrowserBackGuardProps {
  /**
   * Hard block — pushes the current state back on `popstate` without prompting.
   * Use for situations where we can't let the user leave at all
   * (e.g. files mid-upload).
   */
  isBlocked: boolean;
  /**
   * Soft block — triggers the unsaved-changes modal via `guardedNavigate`.
   * When false (and `isBlocked` is also false) normal browser back applies.
   */
  hasUnsavedChanges: boolean;
  /**
   * The navigation-guard helper from `useNavigationGuard`. Called with
   * `onIntercepted` when back is pressed during unsaved changes — the guard
   * surfaces the save/discard/stay modal and only runs the action if the user
   * chooses to leave.
   */
  guardedNavigate: (
    action: () => void | Promise<void>,
    type: 'back',
  ) => void | Promise<void>;
  /**
   * Action that performs the actual "go back" once the modal is resolved —
   * typically navigating to the previous domain (and starting it if needed).
   */
  onIntercepted: () => void | Promise<void>;
}

/**
 * Intercepts the browser back/forward button on the assessment workflow page.
 *
 * Behavior:
 * - Pushes a state entry on mount so we can detect `popstate`.
 * - When `isBlocked`, every back press is swallowed by re-pushing state.
 * - When `hasUnsavedChanges`, the guard modal is shown via `guardedNavigate`;
 *   if the user confirms, `onIntercepted` runs (and decides where to go).
 * - Otherwise, normal browser back applies (we can't intercept after the fact).
 */
export const useBrowserBackGuard = ({
  isBlocked,
  hasUnsavedChanges,
  guardedNavigate,
  onIntercepted,
}: UseBrowserBackGuardProps): void => {
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      window.history.pushState(null, '', window.location.href);
      return;
    }

    const handlePopState = () => {
      if (isBlocked) {
        window.history.pushState(null, '', window.location.href);
        return;
      }

      if (hasUnsavedChanges) {
        window.history.pushState(null, '', window.location.href);
        guardedNavigate(async () => {
          await onIntercepted();
        }, 'back');
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isBlocked, hasUnsavedChanges, guardedNavigate, onIntercepted]);
};
